/**
 * Fighting styles.
 *
 * A style is three things and nothing else: a **loadout** of moves a fighter
 * knows, a set of **stat multipliers**, and one **KO condition** — the single
 * kind of moment that is allowed to finish somebody who is already in danger.
 *
 * All of it is data, in `styles.json`. Which matters more here than anywhere
 * else in this game: a style is the thing a player picks, and every style that
 * needs a code change to exist is a style that will not get made.
 *
 * A fighter equips up to three. The **first is primary** and supplies the KO
 * condition outright; the others only widen the loadout and soften the stats.
 * Nothing about a secondary style changes how you win.
 *
 * Pure. No three.js, no fighter — it is handed style names and a move set, and
 * hands back what a fighter built from them would know and be worth.
 */

import STYLES from './styles.json';
import moveConfig from './moves.json';

export { STYLES };

export class StyleConfigError extends Error {}

/**
 * How much more each style costs than the one before it.
 *
 * A second style that cost nearly what the first did is a purchase nobody
 * thinks about, and a ladder of three is meant to be a career's worth of
 * decisions rather than a shopping list. Doubling is the bar; the file is free
 * to be steeper.
 */
const STEEPNESS = 2;

/** The stats every style declares, and what each of them scales. */
export const STATS = Object.freeze({
  /** How fast every move runs. Shortens startup, active and recovery alike. */
  speed: 'move speed',
  /** What a landed blow is worth. */
  damage: 'damage dealt',
  /** How much of that damage is permanent rather than merely felt. */
  wellnessRatio: 'wellness damage',
  /** How fast the meter fills. Multiplies charisma rather than replacing it. */
  momentum: 'momentum earned',
});

/** The ways a style can be allowed to finish a fight. */
export const KO_TESTS = Object.freeze(['strength', 'kind', 'tag', 'comboEnder', 'limb']);

/** Throws on a style set that cannot be satisfied, so a typo fails at boot. */
export function validateStyles(config = STYLES, { moves = moveConfig.moves } = {}) {
  const styles = config?.styles;
  if (!styles || typeof styles !== 'object') {
    throw new StyleConfigError('style set has no "styles" object');
  }

  for (const [id, style] of Object.entries(styles)) {
    if (!style.name) throw new StyleConfigError(`style "${id}" has no display name`);

    if (!Array.isArray(style.moves) || style.moves.length === 0) {
      throw new StyleConfigError(`style "${id}" knows no moves`);
    }
    for (const move of style.moves) {
      if (!moves[move]) {
        throw new StyleConfigError(`style "${id}" loads unknown move "${move}"`);
      }
    }

    for (const stat of Object.keys(STATS)) {
      if (!(style.stats?.[stat] > 0)) {
        throw new StyleConfigError(`style "${id}" has a ${stat} of ${style.stats?.[stat]}`);
      }
    }

    const ko = style.ko;
    if (!ko || !KO_TESTS.includes(ko.test)) {
      throw new StyleConfigError(`style "${id}" has a KO test of "${ko?.test}"`);
    }
    if (!ko.describe) throw new StyleConfigError(`style "${id}" does not say how it wins`);
    // A condition nothing in the move set can satisfy is a style that can take
    // a fighter to the edge and never once put them over it.
    if (!reachable(ko, style, moves)) {
      throw new StyleConfigError(
        `style "${id}" wins by ${ko.describe}, which nothing in its loadout can do`
      );
    }
  }

  const weights = config.weights ?? {};
  if (!(weights.primary > 0) || !(weights.secondary >= 0)) {
    throw new StyleConfigError('style weights must be positive, secondary may be zero');
  }
  if (!(config.maxStyles >= 1)) {
    throw new StyleConfigError(`maxStyles is ${config.maxStyles}`);
  }

  validateAcquire(config);

  return config;
}

/**
 * Throws on a price ladder that could not mean what it says.
 *
 * **One entry per style that can be bought**, which is `maxStyles` less the one
 * chosen at creation — a fighter picks their primary and buys the rest, so a
 * ladder of the wrong length is a style with no price or a price with no style.
 *
 * Strictly ascending *and* steep, because the interesting failure is not a typo:
 * it is a file edited into a ladder where a third style costs eleven points
 * instead of ten, which reads as tuned and is not a decision anybody makes.
 */
export function validateAcquire(config = STYLES) {
  const { acquire, maxStyles } = config ?? {};
  const want = maxStyles - 1;
  if (!Array.isArray(acquire) || acquire.length !== want) {
    throw new StyleConfigError(
      `there are ${acquire?.length ?? 0} style prices and ${want} styles to buy`
    );
  }
  acquire.forEach((cost, at) => {
    if (!Number.isFinite(cost) || !(cost > 0)) {
      throw new StyleConfigError(`style ${at + 2} costs ${JSON.stringify(cost)}`);
    }
    if (at === 0) return;
    const before = acquire[at - 1];
    if (!(cost >= before * STEEPNESS)) {
      throw new StyleConfigError(
        `style ${at + 2} costs ${cost} against ${before}; want at least ${before * STEEPNESS}`
      );
    }
  });
  return acquire;
}

/**
 * What the next style costs a fighter who already has `known` of them.
 *
 * The first is **free and is not bought**: it is chosen at creation, it is the
 * primary, and it is the only one that decides the KO condition. Every style
 * after it is a secondary bought with development points — which is why this is
 * indexed off how many somebody has rather than off which slot is being filled.
 *
 * `null` for a fighter already carrying the cap, so a caller can tell "nothing
 * left to buy" from "that costs nothing".
 */
export function styleCost(known = 0, config = STYLES) {
  const have = Array.isArray(known) ? known.length : Number(known) || 0;
  if (have < 1 || have >= config.maxStyles) return null;
  return config.acquire[have - 1] ?? null;
}

/**
 * Whether this blow is the kind of moment the style is allowed to win with.
 *
 * Asked *before* the damage lands, because the fight layer has to know whether
 * a blow may finish somebody before it applies it — which is why the limb test
 * asks what the damage *would* do rather than what it did.
 */
export function matchesKO(ko, hit = {}, defender = null) {
  switch (ko?.test) {
    case 'strength':
      return hit.strength === ko.strength;

    case 'kind':
      return (hit.kind ?? 'strike') === ko.kind;

    case 'tag':
      return (hit.tags ?? []).includes(ko.tag);

    // A tagged move that ends a chain, thrown as part of one. Kickboxing does
    // not win with a kick; it wins with a kick you *worked up to*.
    case 'comboEnder':
      return (
        (hit.tags ?? []).includes(ko.tag) &&
        Boolean(hit.ender) &&
        (hit.chainIndex ?? 0) + 1 >= (ko.minChain ?? 2)
      );

    // The only condition that is about the defender rather than the blow: a
    // limb that this blow would finish off. Submissions do not knock anybody
    // out, they take a piece until there is nothing holding it together.
    case 'limb': {
      const part = hit.limbTarget ?? hit.part;
      return Boolean(
        defender?.health?.wouldBreakLimb?.(part, hit.damage, hit.limbScale ?? 1)
      );
    }

    default:
      return false;
  }
}

/** Whether any move in a style's own loadout could ever satisfy its KO test. */
function reachable(ko, style, moves) {
  const loadout = style.moves.map((name) => moves[name]);
  switch (ko.test) {
    case 'strength':
      return loadout.some((move) => move.strength === ko.strength);
    case 'kind':
      return loadout.some((move) => (move.kind ?? 'strike') === ko.kind);
    case 'tag':
      return loadout.some((move) => (move.tags ?? []).includes(ko.tag));
    case 'comboEnder':
      return loadout.some(
        (move) => (move.tags ?? []).includes(ko.tag) && (move.follows ?? []).length === 0
      );
    case 'limb':
      return loadout.some((move) => move.limbTarget);
    default:
      return false;
  }
}

/**
 * What a fighter equipped with these styles knows and is worth.
 *
 * `names` is ordered: the first is primary. Anything past `maxStyles` is
 * refused rather than trimmed, because silently dropping a style a player
 * picked is worse than telling them.
 */
export function createLoadout(
  names,
  {
    config = STYLES,
    moves = moveConfig.moves,
    /**
     * One fighter's own deviation from what their styles give them, as a
     * `{ speed, damage, wellnessRatio, momentum }` around 1.
     *
     * Styles say what somebody *fights like*; this says what they *are*. Two
     * wrestlers both throw a suplex and one of them hits harder, and without
     * this the only way to say so is to give them a style they do not fight.
     */
    spread = null,
  } = {}
) {
  const picked = [...new Set(names ?? [])];
  if (picked.length === 0) throw new StyleConfigError('a fighter needs at least one style');
  if (picked.length > config.maxStyles) {
    throw new StyleConfigError(`${picked.length} styles; at most ${config.maxStyles}`);
  }
  for (const id of picked) {
    if (!config.styles[id]) throw new StyleConfigError(`no such style "${id}"`);
  }

  const primary = config.styles[picked[0]];

  // Moves in loadout order, primary first, with no repeats. A move two styles
  // both teach is still one move.
  const known = [];
  for (const id of picked) {
    for (const move of config.styles[id].moves) if (!known.includes(move)) known.push(move);
  }

  return {
    ids: picked,
    primaryId: picked[0],
    primary,
    styles: picked.map((id) => config.styles[id]),
    /** Move names, in the order the styles teach them. */
    moves: known,
    /** The one condition that can finish somebody. Always the primary's. */
    ko: primary.ko,
    stats: applySpread(combineStats(picked.map((id) => config.styles[id]), config.weights), spread),
    /** The fighter's own deviation, or null. Kept so it survives a style swap. */
    spread,
    /** Follow-ups of a move, filtered to what this fighter actually knows. */
    follows(name) {
      return (moves[name]?.follows ?? []).filter((next) => known.includes(next));
    },
    knows: (name) => known.includes(name),
  };
}

/**
 * One fighter's own deviation, laid over what their styles gave them.
 *
 * **Added, not multiplied**, for the same reason `combineStats` adds: a
 * multiplier on top of a multiplier is how a fighter two styles made 20%
 * faster becomes 45% faster than anybody because somebody also called them
 * quick. A spread of 1 is a no-op, and a stat can be softened but never
 * inverted.
 *
 * A key that is not a stat throws rather than being ignored, because the only
 * thing it can be is a typo — and a typo that is quietly dropped is a fighter
 * who reads as tuned and is not.
 */
export function applySpread(stats, spread = null) {
  if (!spread) return stats;
  const out = { ...stats };
  for (const [stat, value] of Object.entries(spread)) {
    if (!(stat in STATS)) {
      throw new StyleConfigError(`"${stat}" is not a stat; there is ${Object.keys(STATS).join(', ')}`);
    }
    if (!Number.isFinite(value)) {
      throw new StyleConfigError(`spread.${stat} is ${JSON.stringify(value)}; want a number`);
    }
    out[stat] = Math.max(0.1, out[stat] + (value - 1));
  }
  return out;
}

/**
 * How several styles' multipliers become one.
 *
 * Multiplying them together would let three styles that are each 20% faster
 * produce a fighter 73% faster than anybody, which is how stacking systems
 * die. Instead each style's *deviation from 1* is weighted and summed: the
 * primary at full, the others at half.
 *
 * So a fighter's stats always look mostly like their primary style, which is
 * the point of having a primary at all, and picking a third style is a nudge
 * rather than a multiplier.
 */
export function combineStats(styles, weights = STYLES.weights) {
  const combined = {};
  for (const stat of Object.keys(STATS)) {
    let total = 1;
    styles.forEach((style, index) => {
      const weight = index === 0 ? weights.primary : weights.secondary;
      total += (style.stats[stat] - 1) * weight;
    });
    // A stat can be softened but never inverted.
    combined[stat] = Math.max(0.1, total);
  }
  return combined;
}
